import { trackOwnerTransfer, trackUserBaseV2Selector } from '@fullstory/analytics';
import {
  AutoComplete,
  AutoCompleteOptionType,
  Avatar,
  Box,
  Button,
  Modal,
  Spinner,
  Text,
} from '@fullstory/components';
import { intl } from '@fullstory/intl';
import { Role } from '@fullstory/proto/fs/services/orgs';
import { UserInfo } from '@fullstory/redux';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

interface TransferOwnersModalUsers extends UserInfo {
  role: Role;
}

export interface TransferOwnersModalProps {
  users?: TransferOwnersModalUsers[];
  owners?: UserInfo[];
  onSave: (owners: UserInfo[]) => void;
  onClose: () => void;
  open: boolean;
}

export const TransferOwnersModal: FC<TransferOwnersModalProps> = ({
  onClose,
  onSave,
  users,
  owners,
  open,
}) => {
  const [showRemove, setShowRemove] = useState<string>();
  const [searchInput, setSearchInput] = useState<string>('');
  const [pendingOwners, setPendingOwners] = useState(owners ?? []);
  const userBaseV2 = useSelector(trackUserBaseV2Selector);

  useEffect(() => {
    setPendingOwners(owners ?? []);
  }, [owners, open]);

  const filteredOptions: AutoCompleteOptionType[] = useMemo(
    () =>
      users
        ? users
            .map((user): AutoCompleteOptionType => {
              const canAdd = user.role !== Role.EXPLORER && user.role !== Role.VIEW_ONLY;
              return {
                id: user.email,
                label: user.firstName ? `${user.firstName} ${user.lastName}` : user.email,
                subtext: user.firstName ? user.email : undefined,
                tooltip: {
                  tooltipContent: intl.formatMessage({
                    defaultMessage: 'Explorers and guests cannot be added as owners',
                    id: 'vHF1ld',
                  }),
                  disabled: canAdd,
                  placement: 'left',
                },
                disabled: !canAdd,
                leftAdornment: (
                  <Avatar
                    size="sm"
                    name={user.firstName ? `${user.firstName} ${user.lastName}` : user.email}
                    subtitle={user.firstName ? user.email : undefined}
                  />
                ),
              };
            })
            .filter(
              opt =>
                (opt.label.toLowerCase().includes(searchInput.trim().toLowerCase()) ||
                  opt.id.toLowerCase().includes(searchInput.trim().toLowerCase())) &&
                !pendingOwners?.find(a => a.email === opt.id),
            )
        : [],
    [users, searchInput, pendingOwners],
  );

  const removeOwner = (ownerEmail: string) => {
    setPendingOwners(pendingOwners?.filter(owner => owner.email !== ownerEmail));
  };

  const addOwner = (ownerEmail: string) => {
    const user = users?.find(user => user.email === ownerEmail);
    if (user) setPendingOwners([user, ...pendingOwners]);
  };

  // Represented by the number of users in pendingOwners not in owners
  const getNumberOfOwnersAdded = () =>
    pendingOwners.reduce(
      (acc, val) => (owners?.find(o => val.email === o.email) ? acc : acc + 1),
      0,
    );

  // Represented by the number of users in owners not in pendingOwners
  const getNumberOfOwnersRemoved = () =>
    owners
      ? owners.reduce(
          (acc, val) => (pendingOwners?.find(o => val.email === o.email) ? acc : acc + 1),
          0,
        )
      : 0;

  return (
    <Modal open={open} size="small" data-component="TransferOwnersModal" onCloseSignal={onClose}>
      <Modal.Header>
        <FormattedMessage defaultMessage="Modify editors" id="FBYIbD" />
      </Modal.Header>
      <Modal.Body>
        <Box display="flex" flexDirection="column" gap={20}>
          <AutoComplete
            placeholder={intl.formatMessage({
              defaultMessage: 'Add people',
              id: 'Y8lhVt',
            })}
            inputValue={searchInput}
            onInputChange={input => setSearchInput(input)}
            selected={null}
            onSelect={(s: AutoCompleteOptionType) => {
              addOwner(s.id);
              setSearchInput('');
            }}
            options={filteredOptions ?? []}
            persistPopoverOnSelect
          />
          <Box display="flex" gap={12} flexDirection="column">
            <Text appearance="bodyMediumBold">
              <FormattedMessage defaultMessage="Owners" id="srCJeL" />
            </Text>
            {pendingOwners?.length === 0 && <Spinner />}
            <Box display="flex" flexDirection="column" gap={12} overflow="auto" maxHeight={210}>
              {pendingOwners?.map(owner => (
                <Box
                  key={owner.email}
                  display="flex"
                  gap={12}
                  onMouseLeave={() => setShowRemove('')}
                  onMouseOver={() => setShowRemove(owner.email)}
                  alignItems="center"
                >
                  <Avatar
                    name={owner.firstName ? `${owner.firstName} ${owner.lastName}` : owner.email}
                    subtitle={owner.firstName ? owner.email : undefined}
                  />
                  <Box display="flex" flexDirection="column">
                    <Text appearance="bodySmall">
                      {owner.firstName ? `${owner.firstName} ${owner.lastName}` : owner.email}
                    </Text>
                    <Text appearance="bodyXSmall" color="secondary">
                      {owner.email}
                    </Text>
                  </Box>
                  {showRemove === owner.email && pendingOwners.length > 1 && (
                    <Box flex={1} display="flex" justifyContent="flex-end" marginRight={4}>
                      <Button
                        size="small"
                        variant="minimal-destructive"
                        onClick={() => removeOwner(owner.email)}
                      >
                        <FormattedMessage defaultMessage="Remove" id="G/yZLu" />
                      </Button>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Modal.Body>
      <Modal.FooterActions
        primaryAction={{
          action: () => {
            trackOwnerTransfer({
              ...userBaseV2,
              object_type: 'Dashboard',
              num_owners_added: getNumberOfOwnersAdded(),
              num_owners_removed: getNumberOfOwnersRemoved(),
              num_owners_total: pendingOwners.length,
            });
            onSave(pendingOwners);
          },
          text: intl.formatMessage({
            defaultMessage: 'Save',
            id: 'wW7pfG',
            description: 'Save and close popup',
          }),
          disabled: pendingOwners.length === 0,
        }}
        secondaryAction={{
          action: onClose,
          text: intl.formatMessage({
            defaultMessage: 'Cancel',
            id: 'JHJyH+',
            description: 'Cancel and close popup',
          }),
        }}
      />
    </Modal>
  );
};
